import type { DirectDealsListItem } from '@openx/types/directDeals';
import type { RowActionProps } from '@openx/types/tableHelpers';
import { getInitialContactValue } from '@openx/utils/directDealContactValue';
import { isAfter } from 'date-fns';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useRequestDirectDealModalContext } from '../context/RequestDirectDealModalContext';

type UseDirectDealsListRowActions = {
  rowActions: RowActionProps<DirectDealsListItem>[];
};

const canContactPublisher = (publisherId: string): boolean => {
  const localValue = getInitialContactValue(publisherId);
  return !localValue || isAfter(new Date(), new Date(localValue));
};

export const useDirectDealsListRowActions = (): UseDirectDealsListRowActions => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openModal } = useRequestDirectDealModalContext();

  const [contactMap, setContactMap] = useState<Map<string, boolean>>(new Map());

  const seeDetailsRowAction = useMemo<RowActionProps<DirectDealsListItem>>(
    () => ({
      'data-test': 'see-details-button',
      onClick: directDeal => navigate(`/direct-deals/${directDeal.id}/details`),
      label: t('See details'),
      variant: 'text',
    }),
    [navigate, t]
  );

  const connectWithPublisherRowAction = useMemo<RowActionProps<DirectDealsListItem>>(
    () => ({
      allowed: directDeal => {
        const localOverride = contactMap.get(directDeal.id);
        if (typeof localOverride === 'boolean') {
          return localOverride;
        }
        return canContactPublisher(directDeal.id);
      },
      'data-test': 'connect-with-publisher-button',
      label: t('Connect with publisher'),
      onClick: directDeal => {
        openModal({
          publisherId: directDeal.id,
          publisherName: directDeal.name,
          callback: () => setContactMap(prev => new Map(prev).set(directDeal.id, false)),
        });
      },
      notAllowedMessage: t("You've already requested contact with this publisher!"),
    }),
    [t, openModal, contactMap]
  );

  return {
    rowActions: [seeDetailsRowAction, connectWithPublisherRowAction],
  };
};
